<script context="module">
    import { get } from "svelte/store";
    import {
        user,
        showApiErrorModal,
        isRetriedFetch,
        firstFetchErrorMsg,
    } from "../stores";
    import { HTTP_STATUS, X_Device_ID } from "../Utils/utilConstants.svelte";
    import {
        globalAbortController,
        refreshToken,
        showError,
    } from "../Utils/utilFunctions.svelte";
    import {
        serverUrl,
        checkTokenExpiration,
        signOut,
    } from "../Utils/utilFunctions.svelte";

    const doFetch = async (url, data, isManuallyHandleError = false) => {
        const userData = get(user);
        const userToken = userData == null ? "" : userData.token;

        if (userToken) {
            const tokenRefreshResult = await checkTokenExpiration();

            if (
                tokenRefreshResult != "valid" &&
                tokenRefreshResult != "refreshed"
            ) {
                signOut({ reason: "Invalid token" }, true);
                return;
            }

            try {
                var requestObject = {
                    method: "DELETE",
                    headers: {
                        "Content-Type": "application/json",
                        "Access-Control-Allow-Credentials": "true",
                        "Access-Control-Allow-Origin": "*",
                        "Access-Control-Allow-Methods": "DELETE",
                        Authorization: "Bearer " + userToken,
                    },
                    mode: "cors",
                    cache: "no-cache",
                    signal: globalAbortController.signal,
                };

                requestObject.headers["X-Device-Id"] = X_Device_ID;

                if (data) {
                    if (typeof data === "object") data.source = "Dashboard";
                    requestObject.body = JSON.stringify(data);
                }
                const response = await fetch(
                    `${serverUrl}/${url}`,
                    requestObject,
                );

                if (response.ok) {
                    if (response.status === HTTP_STATUS.NO_CONTENT) return null;

                    let res = await response.json();

                    if (res.error && !isManuallyHandleError) {
                        console.log(res.error);
                        showError(
                            "please refresh the page and try again and contact support if the issue persists.",
                        );
                    }

                    return res;
                } else if (
                    response.status == HTTP_STATUS.SERVER_TIMEOUT ||
                    response.status == HTTP_STATUS.BAD_GATE_WAY ||
                    response.status == HTTP_STATUS.SERVICE_UNAVAILABLE ||
                    response.status == HTTP_STATUS.GATEWAY_TIMEOUT
                ) {
                    if (get(isRetriedFetch)) {
                        isRetriedFetch.set(false);
                        firstFetchErrorMsg.set("");
                        showApiErrorModal.set(true);
                        return;
                    }

                    isRetriedFetch.set(true);
                    const resText = await response.text();
                    firstFetchErrorMsg.set(resText);

                    setTimeout(async () => {
                        return await doFetch(url);
                    }, 1000);
                } else if (
                    response.status === HTTP_STATUS.UNAUTHORIZED ||
                    response.status === HTTP_STATUS.FORBIDDEN
                ) {
                    const tokenStatus = await checkTokenExpiration();
                    if (tokenStatus === "valid") {
                        // Refreshed from in another thread?
                        const currentUserData = get(user);
                        if (
                            currentUserData &&
                            currentUserData.token &&
                            currentUserData.token !== userToken
                        ) {
                            return await doFetch(url);
                        }

                        if (get(isRetriedFetch)) {
                            signOut(
                                { url, errorStatus: response.status },
                                true,
                            );
                            isRetriedFetch.set(false);
                            return;
                        }

                        const localSavedUserData = JSON.parse(
                            localStorage.getItem("TciSavedUser"),
                        );

                        if (localSavedUserData) {
                            user.set({
                                ...localSavedUserData,
                            });
                        }

                        await new Promise((resolve) =>
                            setTimeout(resolve, 1000),
                        );

                        isRetriedFetch.set(true);

                        const refreshStatus = await refreshToken();

                        if (refreshStatus) {
                            return await doFetch(url);
                        } else {
                            signOut(
                                { url, errorStatus: response.status },
                                true,
                            );
                            return;
                        }
                    } else if (tokenStatus === "refreshed") {
                        return await doFetch(url);
                    } else {
                        // Already signed out from checkTokenExpiration
                        return;
                    }
                } else {
                    const errText = await response.text();
                    throw new Error(errText);
                }
            } catch (error) {
                throw error;
            }
        } else signOut({ reason: "Invalid token" }, true);
    };

    const deleteAdmin = async (id) => {
        let url = `Admin/${id}`;

        return await doFetch(url);
    };

    const deletePatient = async (id) => {
        let url = `Patient/${id}`;

        return await doFetch(url);
    };

    const deletePatientContact = async (id) => {
        let url = `PatientContact/${id}`;

        return await doFetch(url);
    };

    const deletePatientEncounter = async (id) => {
        let url = `PatientEncounter/${id}`;

        return await doFetch(url);
    };

    const deleteAnnouncement = async (id) => {
        let url = `Announcement/${id}`;

        return await doFetch(url);
    };

    const deleteOrderProduct = async (id) => {
        let url = `OrderProducts/${id}`;

        return await doFetch(url);
    };

    const deleteAgreementDocument = async (id) => {
        let url = `AgreementDocuments/${id}`;

        return await doFetch(url);
    };

    const deleteManager = async (id) => {
        let url = `Manager/${id}`;

        return await doFetch(url);
    };

    const deleteAccountLeave = async (id) => {
        let url = `AccountLeave/${id}`;

        return await doFetch(url);
    };

    // deleteRepeatingType: 0 for this event only and 1 for all related repeating
    const deleteAccountCalendarEvent = async (id, deleteRepeatingType) => {
        let url = `AccountCalendarEvent/${id}/${deleteRepeatingType}`;

        return await doFetch(url);
    };

    // deleteRepeatingType: 0 for this event only and 1 for all related repeating
    const deleteGlobalEvent = async (id, deleteRepeatingType) => {
        let url = `GlobalCalendarEvent/${id}/${deleteRepeatingType}`;

        return await doFetch(url);
    };

    const deleteAccountProvider = async (id) => {
        const url = `AccountProvider/${id}`;

        return await doFetch(url);
    };

    const deleteFileById = async (id) => {
        let url = `File/${id}`;

        return await doFetch(url);
    };

    const deleteAccountDoctor = async (id) => {
        let url = `AccountDoctor/${id}`;

        return await doFetch(url);
    };

    const deleteSupportStaff = async (id) => {
        let url = `SupportStaff/${id}`;

        return await doFetch(url);
    };

    const deleteWorkCoverAuthority = async (id) => {
        let url = `WorkCoverAuthorities/${id}`;

        return await doFetch(url);
    };

    const deleteTrafficAccidentAuthority = async (id) => {
        let url = `TrafficAccidentAuthorities/${id}`;

        return await doFetch(url);
    };

    const deleteEmailTemplate = async (id) => {
        let url = `EmailTemplate/${id}`;

        return await doFetch(url);
    };

    const deleteSmsTemplate = async (id) => {
        let url = `SmsTemplate/${id}`;

        return await doFetch(url);
    };

    const deletePdfTemplate = async (id) => {
        let url = `PdfTemplate/${id}`;

        return await doFetch(url);
    };

    const deleteAccountAvatar = async (imgID) => {
        const url = `image/${imgID}`;

        return await doFetch(url);
    };

    const deleteNote = async (noteID) => {
        let url = `notes/${noteID}`;

        return await doFetch(url);
    };

    const deleteAccountPreset = async (id) => {
        const url = `AccountPresets/${id}`;

        return await doFetch(url);
    };

    const deleteBillingRule = async (id) => {
        const url = `BillingRule/${id}`;

        return await doFetch(url);
    };

    const deleteThirdPartyInsurer = async (id) => {
        const url = `AccountThirdPartyInsurers/${id}`;

        return await doFetch(url);
    };

    const deleteCalendarEventsCategory = async (id) => {
        const url = `CalendarEventsCategories/${id}`;

        return await doFetch(url);
    };

    const deleteDefaultFilters = async (id) => {
        const url = `UsersFilters/${id}`;

        return await doFetch(url);
    };

    const deleteSurgeryRoom = async (id) => {
        const url = `SurgeryRooms/${id}`;

        return await doFetch(url);
    };

    const deleteFreeTextItem = async (data) => {
        const url = `PatientEncounterCode`;

        return await doFetch(url, data);
    };

    const deleteBupaPracticeId = async (data) => {
        const url = `BupaPracticeId/${data.id}`;

        return await doFetch(url, data);
    };

    const deletePreOpInstruction = async (data) => {
        const url = `PreOpInstructions/${data.id}`;

        return await doFetch(url);
    };

    const deleteAccountThirdPartyInsurer = async (data) => {
        const url = `AccountThirdPartyInsurers/${data.id}`;

        return await doFetch(url);
    };

    const deleteHospitalIsNoGapProgram = async (data) => {
        const url = `HospitalsNoGapProgram/${data.id}`;

        return await doFetch(url);
    };

    const deleteHospitalIsNoGapProgramProvider = async (data) => {
        const url = `HospitalsNoGapProgram/Provider/${data.id}`;

        return await doFetch(url);
    };

    const deleteHospitalIsNoGapProgramRule = async (data) => {
        const url = `HospitalsNoGapProgram/SpecialRule/${data.id}`;

        return await doFetch(url);
    };

    const deleteHospitalIsNoGapProgramRules = async (noGapProgramId) => {
        const url = `HospitalsNoGapProgram/${noGapProgramId}/SpecialRules`;

        return await doFetch(url);
    };

    const deleteAccountProviderNoGapProgram = async (id) => {
        const url = `AccountProviderNoGapProgram/${id}`;

        return await doFetch(url);
    };

    const deleteOcrScheduleListLink = async (id) => {
        const url = `OcrSchedule/ArchiveList/${id}`;

        return await doFetch(url);
    };

    export const deleteMethod = {
        deleteAdmin,
        deletePatient,
        deletePatientContact,
        deletePatientEncounter,
        deleteAnnouncement,
        deleteManager,
        deleteAccountLeave,
        deleteAccountCalendarEvent,
        deleteGlobalEvent,
        deleteFileById,
        deleteAccountDoctor,
        deleteAccountProvider,
        deleteSupportStaff,
        deleteWorkCoverAuthority,
        deleteTrafficAccidentAuthority,
        deleteEmailTemplate,
        deleteSmsTemplate,
        deletePdfTemplate,
        deleteAccountAvatar,
        deleteNote,
        deleteAccountPreset,
        deleteBillingRule,
        deleteThirdPartyInsurer,
        deleteCalendarEventsCategory,
        deleteDefaultFilters,
        deleteSurgeryRoom,
        deleteFreeTextItem,
        deleteBupaPracticeId,
        deletePreOpInstruction,
        deleteAccountThirdPartyInsurer,
        deleteHospitalIsNoGapProgram,
        deleteHospitalIsNoGapProgramProvider,
        deleteHospitalIsNoGapProgramRule,
        deleteHospitalIsNoGapProgramRules,
        deleteAccountProviderNoGapProgram,
        deleteOcrScheduleListLink,
        deleteOrderProduct,
        deleteAgreementDocument
    };
</script>
